/* =========================================================================
   Ambient background layers — the one mount/unmount contract shared by every
   decorative school/parade (ui/fishSchool.ts, ui/arkParade.ts).

   Rules every layer inherits by going through here:
   • inert — aria-hidden, mounted as the host's FIRST child so it paints under
     the content styles.css lifts to z-index 1; never focusable.
   • idempotent — an already-correct host is left alone, so calling sync on
     every served question never restarts an animation mid-crossing.
   • reduced motion is a hard no — never mounted, and torn down if the OS
     setting flips mid-round.
   ========================================================================= */
import { prefersReducedMotion } from './momentOverlay';

/**
 * Put the layer in `host` when `on`, take it out when not. Returns true when
 * mounted afterwards. `html()` is only called when a mount actually happens.
 */
export function syncAmbientLayer(
  host: HTMLElement | null,
  layerClass: string,
  html: () => string,
  on: boolean,
): boolean {
  if (!host) return false;
  const existing = host.querySelector(':scope > .' + layerClass) as HTMLElement | null;
  if (!on || prefersReducedMotion()) {
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    return false;
  }
  if (existing) return true; // already running — leave it mid-motion
  const layer = document.createElement('div');
  layer.className = layerClass;
  layer.setAttribute('aria-hidden', 'true');
  layer.innerHTML = html();
  host.insertBefore(layer, host.firstChild);
  return true;
}
